"use client";

import React from "react";
import { Coffee, Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
export default function GetYourFreeQuote() {
  return <section data-section-id="223479" id="get-your-free-quote" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-secondary/20 rounded-lg blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-lg blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground mb-4 flex items-center justify-center gap-3 tracking-tight">
            Let's Build the Future
            <Coffee className="size-8 md:size-10 text-primary" />
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            Ready to elevate your digital presence? Whether you need a high-converting landing page or full social media management, BlinkLab Media is ready to help your business work while you sleep.
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-2xl border border-border/50 p-6 md:p-10 backdrop-blur-md">
          <FormProvider formKey="free_quote_form_contact" formId="free_quote_form_contact" sectionName="Start Your Journey" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          interest: "Web Design",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          interest: {
            required: true
          }
        }} submitText="Get Your Free Quote" successMessage="Thank you! Your request has been received. A BlinkLab strategist will contact you shortly.">
            <JourneyForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground mb-2">Request Received!</h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] border-primary text-primary hover:bg-primary/10 rounded-lg">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <FormField control={control} name="interest" rules={{
      required: "Please select a service."
    }} render={({
      field
    }) => <FormItem className="flex flex-col items-center space-y-4 pb-6 border-b border-border/50">
            <FormLabel className="text-lg font-bold font-default text-foreground">I'm interested in:</FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value} onValueChange={value => {
          if (value) field.onChange(value);
        }} className="flex flex-wrap justify-center gap-2" variant="outline" size="lg">
                <ToggleGroupItem value="Web Design" aria-label="Web Design" className="px-6 rounded-lg border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-default">
                  Web Design
                </ToggleGroupItem>
                <ToggleGroupItem value="Social Media" aria-label="Social Media" className="px-6 rounded-lg border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-default">
                  Social Media
                </ToggleGroupItem>
                <ToggleGroupItem value="SEO" aria-label="SEO" className="px-6 rounded-lg border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-default">
                  SEO
                </ToggleGroupItem>
                <ToggleGroupItem value="Branding" aria-label="Branding" className="px-6 rounded-lg border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-default">
                  Branding
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Alex Johnson" className="flex h-11 w-full rounded-lg border border-input bg-muted/30 px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(803) 555-0123" className="flex h-11 w-full rounded-lg border border-input bg-muted/30 px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Email Address</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="alex@blinklab.media" className="flex h-11 w-full rounded-lg border border-input bg-muted/30 px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Project Details (Optional)</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Tell us about your business goals and timeline..." className="min-h-[120px] resize-y rounded-lg border-input bg-muted/30 focus-visible:ring-primary font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium font-default">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-lg shadow-lg transition-all hover:scale-[1.01] active:scale-[0.99]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : <>
            Get Your Free Quote
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}